import { customerSessionKey } from "utils/constants";
import {
	GET_CUSTOMER, UPDATE_CUSTOMER_STATE, CHANGE_DEFAULT_ADDRESS, UPDATE_CUSTOMER_ADDRESS, CREATE_CUSTOMER_ADDRESS,
	DELETE_CUSTOMER_ADDRESS, GET_CUSTOMER_ORDERS, LOAD_MORE_ORDERS,
	GET_CUSTOMER_ADDRESSES, LOAD_MORE_ADDRESSES, UPDATE_CUSTOMER_INFO
} from "utils/action-types";
import { MAIL_ID_REGEX, CUSTOMER_STATE } from "utils/constants";
import { getCustomerAccessToken, isValidToken } from "utils/oauth";
import getInfluenceDigest from "services/influence-customer-digest";
import { SET_INFLUENCE_DIGEST } from "utils/action-types";

const influenceDigest = (user) => async (dispatch) => {
	const { emailAddress: { emailAddress }, id } = user;
	const digest = await getInfluenceDigest(emailAddress, id);
	if (digest) dispatch({ type: SET_INFLUENCE_DIGEST, payload: digest });
};

export const getCustomerProfile = (refresh = false) => async (dispatch, getState) => {
	const { shopifyClient } = getState().global;
	const sessionCustomer = window.sessionStorage.getItem(customerSessionKey);
	const user = sessionCustomer && !refresh ? JSON.parse(sessionCustomer) : await shopifyClient.customer.getProfile();
	const token = getCustomerAccessToken();

	if (!user || (sessionCustomer && !isValidToken(token))) {
		dispatch({
			type: UPDATE_CUSTOMER_STATE,
			payload: CUSTOMER_STATE.Logged_out,
		});
		return;
	}

	dispatch({
		type: GET_CUSTOMER,
		user,
		customerState: user?.id ? CUSTOMER_STATE.Logged_in : CUSTOMER_STATE?.Logged_out,
	});

	if (user) 
		dispatch(influenceDigest(user));	

	return user;
};

export const fetchCustomerAddresses = (cursor = null) => async (dispatch, getState) => {
	const { shopifyClient } = getState().global;
	const { phoneNumber } = getState().account.profile;
	const customer = await shopifyClient.customer.getPersonalAddresses(cursor);
	if (!customer) return;

	const mappedAddresses = Object.fromEntries(
		customer?.addresses?.nodes.map(addr => {
			// Add the account phone number to each address
			const addressWithPhone = {
				...addr,
				phoneNumber: phoneNumber?.phoneNumber || ""
			};
			return [addr.id.match(MAIL_ID_REGEX)[1], addressWithPhone];
		})
	);

	dispatch({
		type: cursor ? LOAD_MORE_ADDRESSES : GET_CUSTOMER_ADDRESSES,
		addresses: mappedAddresses,
		pageInfo: customer?.addresses?.pageInfo,
		defaultAddress: customer?.defaultAddress,
	});
};

export const updateCustomerDefaultAddress = (addressId) => async (dispatch, getState) => {
	const { shopifyClient } = getState().global;
	const response = await shopifyClient.customer.changeDefaultAddress(addressId);

	if (!response || response?.customerUserErrors?.length) return { success: false };

	const payload = response?.customerAddress;

	dispatch({ type: CHANGE_DEFAULT_ADDRESS, payload });
	return { success: true };
};

export const updateCustomerAddress = (address, addressId, cleanId, defaultAddress) => async (dispatch, getState) => {
	const { shopifyClient } = getState().global;
	const { phoneNumber } = getState().account.profile;

	// Add the user's phone number to the address
	const addressWithPhone = {
		...address,
		phoneNumber: phoneNumber?.phoneNumber || ""
	};

	const response = await shopifyClient.customer.updateAddress(addressId, addressWithPhone, defaultAddress);

	if (!response || response?.userErrors?.length) {
		return { success: false, errors: response?.userErrors };
	}

	const payload = response?.customerAddress;

	dispatch({
		type: UPDATE_CUSTOMER_ADDRESS,
		cleanId,
		payload,
	});

	if (defaultAddress) dispatch({ type: CHANGE_DEFAULT_ADDRESS, payload });

	return { success: true };
};

export const createCustomerAddress = (address, defaultAddress) => async (dispatch, getState) => {
	const { shopifyClient } = getState().global;
	const { phoneNumber } = getState().account.profile;

	// Add the user's phone number to the address
	const addressWithPhone = {
		...address,
		phoneNumber: phoneNumber?.phoneNumber || ""
	};

	const response = await shopifyClient.customer.createAddress(addressWithPhone, defaultAddress);

	if (!response || response?.userErrors?.length) return { success: false, errors: response?.userErrors };

	const payload = response?.customerAddress;

	dispatch({
		type: CREATE_CUSTOMER_ADDRESS,
		payload: { id: payload?.id?.match(MAIL_ID_REGEX)[1], address: payload },
	});

	if (defaultAddress) dispatch({ type: CHANGE_DEFAULT_ADDRESS, payload });

	return { success: true };
};

export const deleteCustomerAddress = (addressId, cleanId) => async (dispatch, getState) => {
	const { shopifyClient } = getState().global;
	const response = await shopifyClient.customer.deleteAddress(addressId);

	if (!response || response?.userErrors?.length) {
		return { success: false };
	}

	dispatch({
		type: DELETE_CUSTOMER_ADDRESS,
		cleanId,
	});

	return { success: true };
};

export const updateCustomerInfo = (customerData) => async (dispatch, getState) => {
	const { shopifyClient } = getState().global;

	const response = await shopifyClient.customer.update(customerData);
  
	const errors = response?.customerUpdate?.customerUserErrors;
	

	if (!response || (errors && errors.length > 0)) {
		return { success: false, errors };
	}
  
	const customer = response?.customer;
	
	if (!customer) {
		return { success: false };
	}

	dispatch({
		type: UPDATE_CUSTOMER_INFO,
		payload: customer
	});
  
	return { success: true };
};
  


export const fetchCustomerOrders = (pageInfo) => async (dispatch, getState) => {
	const customerId = getState().account.profile.id;
	const { shopifyClient } = getState().global;
	const [adminResponse, storefrontResponse] = await Promise.all([
		shopifyClient.customer.getOrdersFromAdmin(customerId, pageInfo?.admin?.endCursor),
		shopifyClient.customer.getOrders(pageInfo?.storefront?.endCursor),
	]).catch((err) => {
		console.error("Error fetching customer orders:", err);
		return [];
	});

	const payload = {
		orders: adminResponse?.orders?.map((order) => {
			const storefrontOrder = storefrontResponse?.orders?.nodes?.find(o => o.name === order.name);
			return {
				...order,
				statusPageUrl: storefrontOrder?.statusPageUrl,
			};
		}),
		pageInfo: {
			admin: adminResponse?.pageInfo,
			storefront: storefrontResponse?.orders?.pageInfo,
		},
	};

	if (adminResponse?.orders?.length && !adminResponse?.customerUserErrors?.length)
		dispatch({
			type: pageInfo?.admin?.endCursor ? LOAD_MORE_ORDERS : GET_CUSTOMER_ORDERS,
			payload,
		});
};
